import { useEffect, useMemo, useState } from "react";
import { usePrevious } from "react-use";
import { skipToken } from "@reduxjs/toolkit/query";
import { useSnackbar } from "notistack";

import {
  apiSlice,
  useExecuteCustomWorkflowMutation,
  useGetCustomWorkflowExecutionLogsQuery,
  useGetCustomWorkflowExecutionsQuery,
  useGetCustomWorkflowsQuery,
  useGetVersionQuery,
  WorkflowExecutionLog,
} from "fond/api";
import { refreshTileSource } from "fond/map/redux";
import { TaskStage, WorkflowKey } from "fond/types";
import { useAppDispatch } from "fond/utils/hooks";
import { getTaskStages } from "fond/utils/taskStages";

type LogsOutput = { type: "waiting" } | { type: "ready"; logs: WorkflowExecutionLog[] };

type Outputs = {
  isLoading: boolean;
  stages: TaskStage[] | null;
  workflowState: undefined | "IN_PROGRESS" | "COMPLETE" | "ERROR" | "PENDING" | "STARTING";
  workflowProgress: undefined | number;
  runWorkflow: null | ((parameters?: object | undefined) => Promise<void>);
  logs: LogsOutput | null;
};

type IProps = {
  // The ID of the version the workflow is associated with.
  versionId: string | undefined;

  // The key of the custom workflow to observe and/or execute.
  key: WorkflowKey | undefined;

  // Called when the workflow is observed moving from an in-progress state to the complete state.
  onComplete?: () => void;

  // Whether version data should be refetched when the workflow fails.
  refreshVersionDataOnFail?: boolean | undefined;
};

export function useCustomWorkflow({ versionId, key, onComplete, refreshVersionDataOnFail }: IProps): Outputs {
  const [pollingInterval, setPollingInterval] = useState<number | null>(null);
  const { refetch: refetchVersion } = useGetVersionQuery(versionId ?? skipToken);
  const dispatch = useAppDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const { data: customWorkflowExecutions, isLoading: isLoadingWorkflows } = useGetCustomWorkflowExecutionsQuery(versionId ?? skipToken, {
    pollingInterval: pollingInterval !== null ? pollingInterval : 30000,
    skip: !key,
  });
  const { data: customWorkflows } = useGetCustomWorkflowsQuery(key ? versionId || skipToken : skipToken);
  const [executeCustomWorkflow, { isLoading, isSuccess }] = useExecuteCustomWorkflowMutation();
  const workflowExecution = useMemo(() => {
    return customWorkflowExecutions && customWorkflowExecutions.CustomWorkflowExecutions.find((execution) => execution.CustomWorkflow.Key === key);
  }, [customWorkflowExecutions, key]);

  const waitingOnLogs = workflowExecution?.LogsStatus === "PENDING";
  // `currentData` here is required to ensure that we don't retain stale log data when the execution ID changes.
  const { currentData: logsData, isFetching: isFetchingLogs } = useGetCustomWorkflowExecutionLogsQuery(workflowExecution?.ID ?? skipToken, {
    skip: waitingOnLogs || workflowExecution?.Status.State !== "ERROR",
  });

  const logs = useMemo(() => {
    if (workflowExecution?.Status.State !== "ERROR") return null;
    if (waitingOnLogs || isFetchingLogs) return { type: "waiting" as const };
    return { type: "ready" as const, logs: logsData?.Logs ?? [] };
  }, [isFetchingLogs, logsData?.Logs, waitingOnLogs, workflowExecution?.Status.State]);

  const workflowState = useMemo(() => {
    if (isLoading || (isSuccess && !workflowExecution)) return "STARTING";
    return workflowExecution ? workflowExecution.Status.State : undefined;
  }, [isLoading, isSuccess, workflowExecution]);
  const stages = useMemo(() => getTaskStages(workflowExecution?.Status.Definition), [workflowExecution?.Status?.Definition]);
  const workflowProgress = workflowExecution && workflowExecution.Status.Progress !== null ? workflowExecution.Status.Progress : undefined;
  const previousWorkflowState = usePrevious(workflowState);

  // Get the custom workflow object
  const workflow = useMemo(() => {
    return customWorkflows && customWorkflows.CustomWorkflows.find((wf) => wf.Key === key);
  }, [customWorkflows, key]);

  // Run the custom workflow
  const runWorkflow = useMemo(() => {
    if (!workflow || !versionId) {
      return null;
    }

    return (parameters: object | undefined) => {
      return executeCustomWorkflow({ versionId, workflowId: workflow.ID, parameters })
        .unwrap()
        .catch(() => {
          enqueueSnackbar("Operation failed. Please try again...", { variant: "error" });
        });
    };
  }, [workflow, executeCustomWorkflow, versionId, enqueueSnackbar]);

  useEffect(() => {
    // Trigger a refresh on the version if we observe the workflow move to a terminal state from elsewhere
    if (
      previousWorkflowState &&
      workflowState !== previousWorkflowState &&
      (workflowState === "COMPLETE" || (refreshVersionDataOnFail && workflowState === "ERROR"))
    ) {
      dispatch(refreshTileSource("project-layers"));
      dispatch(
        apiSlice.util.invalidateTags([
          { type: "FeatureTotals", id: versionId },
          { type: "Layers", id: versionId },
          { type: "VersionRootConfiguration", id: versionId },
        ])
      );
      refetchVersion();
      if (workflowState === "COMPLETE") {
        onComplete?.();
      }
    }

    // Alter the polling interval dynamically based on whether the workflow is currently executing
    if (workflowState === "IN_PROGRESS") {
      setPollingInterval(5000);
    } else if (workflowState === "ERROR" && waitingOnLogs) {
      // Poll until we notice that logs are ready
      setPollingInterval(5000);
    } else {
      setPollingInterval(null);
    }
  }, [dispatch, onComplete, previousWorkflowState, refetchVersion, refreshVersionDataOnFail, versionId, waitingOnLogs, workflowState]);

  return {
    workflowState,
    workflowProgress,
    stages,
    runWorkflow,
    isLoading: isLoadingWorkflows || isLoading,
    logs,
  };
}
